import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, useTheme, Divider } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { selectQlikCalcVariableByName } from '../../../store/slice/qlikVariableSlice';
import QlikVizPanel from '../../qlik-viz-panel/QlikVizPanel';
import QlikKpi from '../../qlik-kpi/QlikKpi';
import { getHyperCubeData, getQlikApp } from '../../../services/qlikService';
import { QLIK_APP_REF } from '../../../constants';
import { useReady } from '../../../hooks/useReady';
import { getQlikResources } from '../../../services/getQlikResources';
import { getVariableContentByName } from '../../../services/qlikVariableService';
import SimpleQlikKpi from '../../simple-qlik-kpi/SimpleQlikKpi';
import './PdfReport.scss';

// This component renders the actual PDF report pages for printing
// It matches the Angular pdf-report.component.html structure

const PdfReport: React.FC = () => {
  const hyperCubeCustomIdPdfReport = "pdf-report|claim-industry-class";
  const theme = useTheme();
  const [claimsScenario, setClaimsScenario] = useState('');
  const [industry, setIndustry] = useState('');
  const [indClass, setIndClass] = useState('');

  const calcVariableName = 'calcVars';
  const qlikCalcVariables = useSelector((state: RootState) => selectQlikCalcVariableByName(state, calcVariableName));
  const reportFor = useSelector((state: RootState) => state.uiProps.pdfReportFor);
  const reportBy = useSelector((state: RootState) => state.uiProps.pdfReportBy);

  // Qlik HyperCube
  const hyperCubePdfReport = useReady(QLIK_APP_REF, hyperCubeCustomIdPdfReport).hyperCubeDataCustomId;
  const pdfReportHyperCubeData = useSelector(
    (state: RootState) =>
        state.qlikObject.qlikHyperCubesData[QLIK_APP_REF]?.[hyperCubeCustomIdPdfReport]
  );

  useEffect(() => {
    const initialize = async () => {
        if(!hyperCubePdfReport) {
            await loadCubeData();
        }
        await loadVariables();
    };
    initialize();
  }, [hyperCubePdfReport]);

  useEffect(() => {
    if (pdfReportHyperCubeData) {
        const matrix = pdfReportHyperCubeData.qDataPages[0].qMatrix;
        if (matrix && matrix.length > 0) {
            setClaimsScenario(matrix[0][0].qText);
            setIndustry(matrix[0][1].qText);
            setIndClass(matrix[0][2].qText);
        }
    }
  }, [pdfReportHyperCubeData]);

  const loadCubeData = async () => {
    try {
      const qlikApp = await getQlikApp(QLIK_APP_REF);
      if (!qlikApp) return;

      const pdfReportHyperCubeDef = {
        qDimensions: [
          {
            qDef: {
              qFieldDefs: ['Claim Scenario Text'],
            },
          },
          {
            qDef: {
              qFieldDefs: ['Industry'],
            },
          },
          {
            qDef: {
              qFieldDefs: ['Class Description'],
            },
          },
        ],
        qMeasures: [
          {
            qDef: {
              qDef: '1',
            },
          },
        ],
        qInitialDataFetch: [
          {
            qTop: 0,
            qLeft: 0,
            qHeight: 1,
            qWidth: 3,
          },
        ],
      };

      await getHyperCubeData(
        QLIK_APP_REF,
        hyperCubeCustomIdPdfReport,
        pdfReportHyperCubeDef
      );    
    } catch (error) {
      console.error('Error loading cube data:', error);
    }
  };

  const loadVariables = async () => {
    try {
      
      // Load variable names from config
      const varsConfig = await getQlikResources('qlik-variables');
      const recVarsNames1 = varsConfig?.recVarsNames1 || [];

      // Load each variable's content
      for (const varObj of recVarsNames1) {
        const varName = Object.keys(varObj)[0];
        const varConfig = varObj[varName];
        await getVariableContentByName(calcVariableName, varName, varConfig.appRef);
      }
    } catch (error) {
      console.error('Error loading variables:', error);
    }
  };

  return (
    <Box 
        id="indexReport"
        sx={{ '@media print': { display: 'block' } }}>
      {/* Page 1 */}
      <Box
        id="pdfPage1"
        sx={{
          pageBreakAfter: 'always',
          px: 2, // mimic Container horizontal padding
        //   mx: 'auto', // center
        }}
        className='page'
      >
        {/* Header */}
        <Box 
            className="flexrow page-header" 
            // sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}
        >
          <Box className="brand-logo">
            {/* <img src="/assets/images/logos/chubb_logo_black.svg" alt="Chubb" style={{ height: '36px' }} /> */}
            <svg id="CHUBB_Logo_Black_T" xmlns="http://www.w3.org/2000/svg" width="356.2" height="36.024" viewBox="0 0 356.2 36.024">
                <path id="Fill-1" d="M49.058,6.584V.158H9.206C3.087.158,0,3.294,0,8.386v19.54c0,5.092,3.087,8.228,9.206,8.228H49.058V29.725H6.943V6.584Zm26.587,8.28V.158H68.857v36h6.787V21.19H111.9V36.153h6.79v-36H111.9V14.864ZM184.611.158V29.725H148.874V.158h-6.79V27.926c0,5.092,3.087,8.228,9.206,8.228H182.2c6.119,0,9.206-3.136,9.206-8.228V.158Zm71.942,36c6.683,0,9.77-2.777,9.77-7.56V24.379a3.157,3.157,0,0,0-1.441-2.777l-4.319-3.7,4.319-3.7a3.16,3.16,0,0,0,1.441-2.777v-3.7c0-4.782-3.087-7.557-9.77-7.557H214.8v36ZM221.585,15.02h37.8V6.277h-37.8Zm0,15.015h37.8v-9h-37.8Zm107.989,6.119c6.686,0,9.77-2.777,9.77-7.56V24.379A3.157,3.157,0,0,0,337.9,21.6l-4.319-3.7,4.319-3.7a3.16,3.16,0,0,0,1.441-2.777v-3.7c0-4.782-3.084-7.557-9.77-7.557H287.818v36ZM294.606,15.02h37.8V6.277h-37.8Zm0,15.015h37.8v-9h-37.8Z" transform="translate(0 -0.129)" fill-rule="evenodd"/>
                <path id="Fill-2" d="M138.579,4.758a1.8,1.8,0,0,0,.546-.112.833.833,0,0,0,.382-.3.966.966,0,0,0,.146-.562.916.916,0,0,0-.112-.486.86.86,0,0,0-.3-.3,1.273,1.273,0,0,0-.429-.151,3.059,3.059,0,0,0-.5-.039h-1.279V4.778h.923C138.163,4.778,138.371,4.771,138.579,4.758Zm-2.249,3.134V2.22h2.177a2.044,2.044,0,0,1,1.4.4,1.5,1.5,0,0,1,.442,1.168,1.76,1.76,0,0,1-.112.648,1.376,1.376,0,0,1-.309.468,1.588,1.588,0,0,1-.455.3,2.681,2.681,0,0,1-.549.164l1.688,2.52H139.8l-1.6-2.52h-1.173v2.52Zm.125-6.714a4.038,4.038,0,0,0-2.177,2.229,4.326,4.326,0,0,0-.309,1.649,4.317,4.317,0,0,0,.309,1.649,4.032,4.032,0,0,0,2.177,2.231,4.163,4.163,0,0,0,4.611-.9,4.222,4.222,0,0,0,1.186-2.983,4.282,4.282,0,0,0-.315-1.649,4.19,4.19,0,0,0-.871-1.331A4.1,4.1,0,0,0,138.111.847,4.087,4.087,0,0,0,136.454,1.177Zm3.594-.653a4.723,4.723,0,0,1,1.558,1.043,4.834,4.834,0,0,1,1.035,1.563,4.939,4.939,0,0,1,.377,1.924,4.931,4.931,0,0,1-.377,1.932,4.751,4.751,0,0,1-2.593,2.585,5.172,5.172,0,0,1-3.877,0,4.751,4.751,0,0,1-2.593-2.585,4.935,4.935,0,0,1-.377-1.932,4.943,4.943,0,0,1,.377-1.924,4.883,4.883,0,0,1,1.035-1.563A4.728,4.728,0,0,1,136.171.524a5.172,5.172,0,0,1,3.877,0Z" transform="translate(213.182 -0.147)" fill-rule="evenodd"/>
            </svg>
          </Box>
          <Typography color="white"variant="h6">Chubb Cyber Index®</Typography>
        </Box>

        {reportFor && (
          <Typography variant="h5" align="center" gutterBottom>
            Report prepared for {reportFor}
          </Typography>
        )}

        <Typography className= "report-intro" variant="body1" sx={{ mb: 3, lineHeight: 1.4 }}>
          This report is brought to you by the{' '}
          <a href="https://chubbcyberindex.com">Chubb Cyber Index</a> which provides data driven insights on current
          cyber trends, as well as assessments, calculators and thought leadership to protect your organization. Chubb
          has handled cyber claims for more than two decades. We analyze our data along with public trends to help us
          continuously improve, provide insight to our stakeholders, and help reduce exposures to future losses. Use
          Chubb's interactive Chubb Cyber Index to gain insight you can use today to protect your organization
          tomorrow. For more information on Chubb's cyber offerings visit: https://chubb.com/cyber
        </Typography>

        <Divider />

        {/* Selections */}
        <Box id="pdf-selections" sx={{ fontSize: '12px' }}>
          {industry && (
            <Box component="span" sx={{ mr: 2, display: 'inline-block' }}>
              <Typography component="span" variant="caption" fontWeight="bold">
                Industry:
              </Typography>{' '}
              <Typography component="span" variant="caption" fontStyle='italic'>{industry}</Typography>
            </Box>
          )}
          {indClass && (
            <Box component="span" sx={{ mr: 2, display: 'inline-block' }}>
              <Typography component="span" variant="caption" fontWeight="bold">
                Industry Class:
              </Typography>{' '}
              <Typography component="span" variant="caption" fontStyle='italic'>{indClass}</Typography>
            </Box>
          )}
          {qlikCalcVariables?.vQRevenue && (
            <Box component="span" sx={{ mr: 2, display: 'inline-block' }}>
              <Typography component="span" variant="caption" fontWeight="bold">
                Annual Revenue:
              </Typography>{' '}
              <Typography component="span" variant="caption" fontStyle='italic'>USD ${qlikCalcVariables.vQRevenue.toLocaleString()}</Typography>
            </Box>
          )}
          {qlikCalcVariables?.vUserRecordCount && (
            <Box component="span" sx={{ mr: 2, display: 'inline-block' }}>
              <Typography component="span" variant="caption" fontWeight="bold">
                User Record Count:
              </Typography>{' '}
              <Typography component="span" variant="caption" fontStyle='italic'>{qlikCalcVariables.vUserRecordCount.toLocaleString()}</Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Claims Growth Chart */}
        <Box id="report-claims" sx={{ mb: 3 }}>
          <QlikVizPanel
            qlikVizName="pdf-report-global-inc-growth"
            manualLegend={[
              { name: 'Overall', color: '#000' },
              { name: 'Professional Services', color: '#150F96' },
              { name: 'Technology', color: '#01C1D6' },
              { name: 'Manufacturing', color: '#F1352B' },
              { name: 'Healthcare', color: '#ffb617' },
              { name: 'Financial Institutions', color: '#4B4E53' },
              { name: 'Public Entity', color: '#AFAFAF' },
              { name: 'Education', color: '#7ACB00' },
              { name: 'Retail/Hospitality', color: '#FF6600' },
              { name: 'Entertainment/Media', color: '#6E27C5' },
              { name: 'Other', color: '#FF0198' },
            ]}
            alwaysShow="Overall"
            showCriteria
            print
          />
        </Box>

        {/* Pie Charts Row */}
        <Grid container spacing={2} id="report-pies">
          <Grid size={{ xs: 12, sm: 4 }}>
            <QlikVizPanel 
                flexColumn
                qlikVizName="pdf-report-act-cau-inc-pie-1"
                legendName="inc-growth-actions-legend-pie-1"
                print
             />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <QlikVizPanel 
                flexColumn
                qlikVizName="pdf-report-act-cau-inc-pie-2"
                legendName="inc-growth-assess-actors-legend-pie-2"
                print
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box className="content-block" id="claims-scenario">
              <Typography variant="h6" gutterBottom >
                Common Industry Claims Scenario
              </Typography>
              <Typography sx={{fontSize: "9pt", lineHeight: "12pt"}}>{claimsScenario}</Typography>
            </Box>
          </Grid>
        </Grid>

        {reportBy && (
          <Box className="agnecyName" sx={{ mt: 3 }}>
            <Typography variant="body2">Report prepared by {reportBy}</Typography>
          </Box>
        )}
      </Box>

      {/* Page 2 */}
      <Box
        id="pdfPage2"
        sx={{
          px: 2,
          mx: 'auto',
        }}
        className='page'
      >
        {/* Header */}
        <Box 
            className="flexrow page-header" 
            // sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}
        >
          <Box className="brand-logo">
            {/* <img src="/assets/images/logos/chubb_logo_black.svg" alt="Chubb" style={{ height: '36px' }} /> */}
            <svg id="CHUBB_Logo_Black_T" xmlns="http://www.w3.org/2000/svg" width="356.2" height="36.024" viewBox="0 0 356.2 36.024">
                <path id="Fill-1" d="M49.058,6.584V.158H9.206C3.087.158,0,3.294,0,8.386v19.54c0,5.092,3.087,8.228,9.206,8.228H49.058V29.725H6.943V6.584Zm26.587,8.28V.158H68.857v36h6.787V21.19H111.9V36.153h6.79v-36H111.9V14.864ZM184.611.158V29.725H148.874V.158h-6.79V27.926c0,5.092,3.087,8.228,9.206,8.228H182.2c6.119,0,9.206-3.136,9.206-8.228V.158Zm71.942,36c6.683,0,9.77-2.777,9.77-7.56V24.379a3.157,3.157,0,0,0-1.441-2.777l-4.319-3.7,4.319-3.7a3.16,3.16,0,0,0,1.441-2.777v-3.7c0-4.782-3.087-7.557-9.77-7.557H214.8v36ZM221.585,15.02h37.8V6.277h-37.8Zm0,15.015h37.8v-9h-37.8Zm107.989,6.119c6.686,0,9.77-2.777,9.77-7.56V24.379A3.157,3.157,0,0,0,337.9,21.6l-4.319-3.7,4.319-3.7a3.16,3.16,0,0,0,1.441-2.777v-3.7c0-4.782-3.084-7.557-9.77-7.557H287.818v36ZM294.606,15.02h37.8V6.277h-37.8Zm0,15.015h37.8v-9h-37.8Z" transform="translate(0 -0.129)" fill-rule="evenodd"/>
                <path id="Fill-2" d="M138.579,4.758a1.8,1.8,0,0,0,.546-.112.833.833,0,0,0,.382-.3.966.966,0,0,0,.146-.562.916.916,0,0,0-.112-.486.86.86,0,0,0-.3-.3,1.273,1.273,0,0,0-.429-.151,3.059,3.059,0,0,0-.5-.039h-1.279V4.778h.923C138.163,4.778,138.371,4.771,138.579,4.758Zm-2.249,3.134V2.22h2.177a2.044,2.044,0,0,1,1.4.4,1.5,1.5,0,0,1,.442,1.168,1.76,1.76,0,0,1-.112.648,1.376,1.376,0,0,1-.309.468,1.588,1.588,0,0,1-.455.3,2.681,2.681,0,0,1-.549.164l1.688,2.52H139.8l-1.6-2.52h-1.173v2.52Zm.125-6.714a4.038,4.038,0,0,0-2.177,2.229,4.326,4.326,0,0,0-.309,1.649,4.317,4.317,0,0,0,.309,1.649,4.032,4.032,0,0,0,2.177,2.231,4.163,4.163,0,0,0,4.611-.9,4.222,4.222,0,0,0,1.186-2.983,4.282,4.282,0,0,0-.315-1.649,4.19,4.19,0,0,0-.871-1.331A4.1,4.1,0,0,0,138.111.847,4.087,4.087,0,0,0,136.454,1.177Zm3.594-.653a4.723,4.723,0,0,1,1.558,1.043,4.834,4.834,0,0,1,1.035,1.563,4.939,4.939,0,0,1,.377,1.924,4.931,4.931,0,0,1-.377,1.932,4.751,4.751,0,0,1-2.593,2.585,5.172,5.172,0,0,1-3.877,0,4.751,4.751,0,0,1-2.593-2.585,4.935,4.935,0,0,1-.377-1.932,4.943,4.943,0,0,1,.377-1.924,4.883,4.883,0,0,1,1.035-1.563A4.728,4.728,0,0,1,136.171.524a5.172,5.172,0,0,1,3.877,0Z" transform="translate(213.182 -0.147)" fill-rule="evenodd"/>
            </svg>
          </Box>
          <Typography color="white"variant="h6">Chubb Cyber Index®</Typography>
        </Box>

        {/* Total incident costs */}
        <Box sx={{ mb: 1 }}>
          <QlikVizPanel 
            qlikVizName="pdf-report-tot-claim-cost"
            legendName="pdf-report-tot-claim-cost"
            print
           />
          <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
            * The "Other First Party Costs" category may include other types of losses such as business interruption and
            ransomware payments.
            <br />
            ** The "Third Party Costs" category may include other types of losses, including PCI assessments, regulatory
            fines, and defense and settlements of third-party matters.
          </Typography>
        </Box>

        {/* Calculators Row */}
        <Grid container spacing={2} id="report-calcs">
          {!qlikCalcVariables?.vUserRecordCount && (
            <Grid size={{ xs: 12, sm: 6 }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 12 }}>
                        <QlikKpi qlikVizName="risk-calc-tot-estimate-records" backColor="#6E27C5" />
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <QlikKpi qlikVizName="pdf-report-ind-min" backColor="#4B4E53" short />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <QlikKpi qlikVizName="pdf-report-ind-max" backColor="#01C1D6" short />
                    </Grid>
                </Grid>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: qlikCalcVariables?.vUserRecordCount ? 12 : 6 }} sx={{ ...(qlikCalcVariables?.vUserRecordCount && { marginLeft: 'auto', marginRight: 'auto' }) }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12 }}>
                    <QlikKpi qlikVizName="pdf-report-tot-cyb-inc-costs" backColor="#150F96" />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <SimpleQlikKpi qlikVizName="risk-calc-inc-investigation"/>
                    <SimpleQlikKpi qlikVizName="risk-calc-crisis-mgt"/>
                    <SimpleQlikKpi qlikVizName="risk-calc-pci"/>
                    <SimpleQlikKpi qlikVizName="risk-calc-fines-penalties"/>
                    <SimpleQlikKpi qlikVizName="risk-calc-ransomware"/>
                    <SimpleQlikKpi qlikVizName="risk-calc-data-restoration"/>
                    <SimpleQlikKpi qlikVizName="risk-calc-biz-interruption"/>
                </Grid>
                <Grid size={{ xs: 12 }} textAlign="right">
                    <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                        *In partnership with NetDiligence
                    </Typography>
                </Grid>
            </Grid>
          
          </Grid>
        </Grid>

        {/* PPI Charts */}
        <Grid container spacing={2} id="report-ppi">
          <Grid size={{ sm: 6 }}>
            <QlikVizPanel qlikVizName="pdf-report-inforce-prim-lim-dist" flexColumn print />
          </Grid>
          <Grid size={{ sm: 6 }}>
            <QlikVizPanel qlikVizName="pdf-report-inforce-deduc-dist" flexColumn print />
          </Grid>
        </Grid>

        {/* Disclaimer */}
        <Box className="page-disclaimer" sx={{ mt: 3 }}>
          <Typography 
            component="small"
            sx={{
                color: theme.custom.darkGrey,
                fontSize: "10.2px",
                lineHeight: "9pt",
                fontFamily: "Arial, Helvetica, sans-serif",
            }}
        >
            Chubb is the marketing name used to refer to subsidiaries of Chubb Limited providing insurance and related services. For a list of these subsidiaries, please visit www.chubb.com. Insurance is provided by ACE American Insurance Company and its U.S. based Chubb underwriting company affiliates. All products may not be available in all states. Surplus lines insurance is sold only through licensed surplus lines producers. The information provided is for educational and informational purposes only and should not be relied on as technical, insurance, or legal advice or a definitive statement of the law in any jurisdiction. The claim scenarios provided are hypothetical and are offered solely to illustrate the types of situations that may result in claims. These scenarios should not be compared to actual claims. Coverage is subject to the language of the policies as actually issued. All cost estimates provided are for educational purposes only and to illustrate reasonably foreseeable outcomes. They should not be relied upon alone in determining limits of insurance to purchase. Peer purchasing insights provide insight into what other organizations are purchasing in regard to primary policy limits and deductibles. However, every situation is different and each organization should consult with their licensed insurance agent or broker for help determining appropriate limits of insurance to purchase.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PdfReport;
